import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import { useNavigate } from "react-router-dom";

import AuthRequests from "@/api/auth/auth.requests.ts";
import { setProfileInfo, setToken } from "@/api/tokens.ts";
import UserRequests from "@/api/user/user.requests";
import { ROUTES } from "@/config/routes.ts";

export const LOGIN_KEY = {
  getSessions: (token: string) => ["sessions", token],
  profileList: ["profiles"],
};

interface LoginPasswordData {
  username: string;
  password: string;
}

export const usePostCheckUsername = () => {
  return useMutation({
    mutationFn: ({ username }: { username: string }) => AuthRequests.postCheckUsername(username),
  });
};

export const usePostSendCode = () => {
  return useMutation({
    mutationFn: (username: string) => AuthRequests.postSendCode(username),
  });
};

export const useGoogleSignin = () => {
  return useMutation({
    mutationFn: (formData: FormData) => AuthRequests.googleLogin(formData),
  });
};

// check the otp code
export const usePostCheckCode = () => {
  return useMutation({
    mutationFn: (data: { account_id: number; code: string }) => AuthRequests.postCheckResetOtp(data),
  });
};

// check the otp code and get token. Request registers the user automatically, so we need to use it just before setting the password. In this case, user will be registered and also password will be set. The situation when user is registered but password is not set is not possible in this case.
export const usePostCheckCodeGetToken = () => {
  return useMutation({
    mutationFn: ({ username, code }: { username: string; code: string }) =>
      AuthRequests.postCheckCodeGetToken(username, code),
  });
};

// set the password right after the user is registered
export const usePostSetPassword = () => {
  return useMutation({
    mutationFn: ({ code, password }: { code: string; password: string }) =>
      AuthRequests.postSetPassword(code, password),
  });
};

// register the user and set the password in one request
export const useRegisterUser = () => {
  const navigate = useNavigate();
  const { mutate: mutateToken, isPending: pendingToken, isError: pendingError } = usePostCheckCodeGetToken();
  const { mutate: mutatePassword, isPending: pendingPassword, isSuccess, isError: mutateError } = usePostSetPassword();

  const mutate = ({ username, code, password }: { username: string; code: string; password: string }) => {
    mutateToken(
      { username, code },
      {
        onSuccess: ({ data }) => {
          setToken(data.token);
          mutatePassword(
            { code, password },
            {
              onSuccess: () => {
                navigate(ROUTES.GATE.PROFILES);
              },
            },
          );
        },
      },
    );
  };

  const isPending = pendingToken || pendingPassword;
  const isError = pendingError || mutateError;

  return { mutate, isPending, isSuccess, isError };
};

export const usePostLoginPassword = () => {
  return useMutation({
    mutationFn: (data: LoginPasswordData) => AuthRequests.postLoginPassword(data),
    onSuccess: async (data) => {
      if (data.name === "AxiosError") {
        throw data;
      }
      if (data.data.is_active) {
        const { setToken } = await import("@/api/tokens.ts");
        setToken(data.data.token);
      }
    },
  });
};

export const useUsernameAvailability = () => {
  return useMutation({
    mutationFn: (username: string) => AuthRequests.postUsernameAvailability(username),
  });
};

export const useSuspenseSessions = (token: string) => {
  return useSuspenseQuery({
    queryKey: LOGIN_KEY.getSessions(token),
    queryFn: () => AuthRequests.getSessions(token),
  });
};

export const useDeleteSession = () => {
  return useMutation({
    mutationFn: (data: { token: string; key: string }) => AuthRequests.postSessionDelete(data),
  });
};

export const useResetOtp = () => {
  return useMutation({
    mutationFn: (username: string) => AuthRequests.postResetOtp(username),
  });
};

export const useCheckResetOtp = () => {
  return useMutation({
    mutationFn: (data: { account_id: number; code: string }) => AuthRequests.postCheckResetOtp(data),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: { code: string; password: string; account_id: number }) => AuthRequests.postResetPassword(data),
  });
};

export const useSelectProfileList = () => {
  return useSuspenseQuery({
    queryKey: LOGIN_KEY.profileList,
    queryFn: () => AuthRequests.getSelectProfileList(),
  });
};

export const useJwtCreate = (data?: { profileId: string; age?: string }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (data: { profile_id: string; pin_code?: string }) => AuthRequests.postCreateJwtToken(data),
    onSuccess: async (res) => {
      if (data) {
        const profile = await UserRequests.getProfileDetail(+data.profileId);
        setProfileInfo({ ...res, ...data, age_restrictions: profile.age_restrictions, age: profile.age?.toString() });
        navigate(ROUTES.MAIN.MAIN);
        queryClient.invalidateQueries();
        queryClient.fetchQuery({ queryKey: LOGIN_KEY.profileList });
      }
    },
  });
};

export const useVerifyAccess = () => {
  return useMutation({
    mutationFn: (data: { token: string }) => AuthRequests.postVerifyAccessToken(data.token),
    onSuccess: ({ age, profile_id, age_restrictions }) => {
      const access = localStorage.getItem("splay:access");
      const refresh = localStorage.getItem("splay:refresh");
      if (!access || !refresh) return;
      setProfileInfo({
        access,
        refresh,
        age: age.toString(),
        profileId: profile_id.toString(),
        age_restrictions: age_restrictions,
      });
    },
  });
};

export const useRefreshAccess = () => {
  return useMutation({
    mutationFn: (data: { token: string }) => AuthRequests.postRefreshAccessToken(data.token),
  });
};
